import { useEffect, useRef, useState } from "react";

/**
 * useInView — one-shot intersection observer hook.
 *
 * Returns a `ref` to attach to any element + a `hasEntered` boolean that
 * flips true the FIRST time the element enters the viewport (within the
 * configured `rootMargin` band) and stays true thereafter.
 *
 * The one-shot semantics are deliberate: callers use this to trigger
 * entrance animations or hydrate-once content. Re-firing on every scroll
 * would defeat the "happens once" UX intent and create distracting
 * re-animation on bidirectional scrolling.
 *
 * Respects `prefers-reduced-motion: reduce` — when reduced motion is
 * preferred, `hasEntered` returns true immediately (so any animation
 * that gates on it just renders the final state without animating).
 *
 * Iter-160 (Phase-4 follow-up). First consumer: SavingsCalculator's
 * savings card entrance animation.
 */
export default function useInView({
  rootMargin = "0px 0px -10% 0px",
  threshold = 0.2,
} = {}) {
  const ref = useRef(null);
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    // Reduced-motion: flip immediately to render the final state.
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setHasEntered(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // Server-side / very old browsers — skip observation, show content.
      setHasEntered(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEntered(true);
          obs.disconnect();
        }
      },
      { rootMargin, threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, threshold]);

  return { ref, hasEntered };
}
